/*  iceberg-ui  */
import { IconWithVariant } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Box, Typography } from '@mui/material';

/* Shared */
import { DarkTooltip } from 'application/Shared/Components/Tooltips';
import {
    FontSizes,
    INTEGRATION_ID_VARS,
    INTEGRATION_NAME_VARS
} from 'application/Shared/Constants';
import { Notify } from 'application/Shared/Helpers';
import { ClientLink } from 'application/Shared/types/clientLink';

/*  React  */
import React from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

const MisconfiguredClientWarning = () => {
    const { t } = useTranslation();
    const { partnerId } = useParams();

    return (
        <Link
            to={`/partner/${partnerId}/integrations/partnerLevel`}
            data-testid="partnerClientsMisconfiguredClientWarning"
            data-trackid="partnerClientsMisconfiguredClientWarning"
        >
            <DarkTooltip arrow title={t('Tooltips.Clients.misconfigured')}>
                <Box>
                    <IconWithVariant
                        data-testid="warningIconMisconfigured"
                        variant="warningDark"
                        src="WarningTriangleFilled"
                        size={FontSizes[0]}
                        wrapperProps={{
                            display: 'flex',
                            marginLeft: '4px'
                        }}
                    />
                </Box>
            </DarkTooltip>
        </Link>
    );
};

interface ClientIdProps {
    row: ClientLink;
}

export const ClientId = ({ row }: ClientIdProps) => {
    const { t } = useTranslation();
    const { clientId, tenant } = row;
    const { name, partner } = tenant || {};

    if (partner) {
        return (
            <small data-testid="clientIdPartner">
                {t('Common.internalTenant')}
            </small>
        );
    }

    if (!clientId) {
        return (
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex'
                }}
                data-testid="clientIdNoClientId"
            >
                <small>{t('Common.no_client_id')}</small>
                <MisconfiguredClientWarning />
            </Box>
        );
    }

    const isClientMisconfigured =
        !name ||
        INTEGRATION_ID_VARS.includes(clientId) ||
        INTEGRATION_NAME_VARS.includes(name) ||
        name?.trim().length === 0 ||
        clientId?.trim().length === 0;

    return (
        <CopyToClipboard
            text={clientId}
            onCopy={() => {
                Notify.s(t('Common.copied'));
            }}
        >
            <Box
                data-testid="clientId"
                sx={{
                    alignItems: 'center',
                    cursor: 'pointer',
                    display: 'flex',
                    fontSize: FontSizes[0]
                }}
            >
                <IconWithVariant
                    variant="button"
                    src="Copy"
                    size={FontSizes[0]}
                    wrapperProps={{ marginRight: '2px' }}
                />
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex'
                    }}
                >
                    <DarkTooltip arrow title={t('Tooltips.copy_client_id')}>
                        <Typography fontSize={FontSizes[0]} fontWeight={600}>
                            {clientId}
                        </Typography>
                    </DarkTooltip>
                    {isClientMisconfigured && <MisconfiguredClientWarning />}
                </Box>
            </Box>
        </CopyToClipboard>
    );
};
